import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import createApp from "@shopify/app-bridge";
import { Redirect } from "@shopify/app-bridge/actions";
import { apiCall } from "../helper/Common";
import { SHOPIFY_API_KEY } from "../config/env";
import { FREE_TEMPLATE_ID } from "../utils/planAccess";

const BillingContext = createContext(null);

const LEGACY_STATE = {
  ready: true,
  loading: false,
  isLegacy: true,
  showPlans: false,
  planCode: "legacy",
  maxEvents: null,
  allowedTemplateIds: null,
  registrationEnabled: true,
  plans: [],
  pendingConfirmationUrl: null,
  error: null,
};

function redirectToShopifyConfirmation(url) {
  if (!url || typeof window === "undefined") return;
  const host = new URLSearchParams(window.location.search).get("host");
  try {
    const app =
      window.__shopifyAppBridgeInstance ||
      (host && SHOPIFY_API_KEY ? createApp({ apiKey: SHOPIFY_API_KEY, host }) : null);
    if (app) {
      Redirect.create(app).dispatch(Redirect.Action.REMOTE, url);
      return;
    }
  } catch (err) {
    console.warn("[billing] App Bridge redirect failed", err);
  }
  if (window.top) window.top.location.href = url;
  else window.location.href = url;
}

export function BillingProvider({ children }) {
  const shopInfo = useSelector((state) => state.install.installData?.shop_info);
  const installReady = useSelector((state) => state.install.checked && state.install.success);
  const explicitNew = String(shopInfo?.user_type) === "0";
  const [state, setState] = useState({ ...LEGACY_STATE, ready: false, loading: true });

  const refresh = useCallback(async () => {
    if (!explicitNew) {
      setState(LEGACY_STATE);
      return LEGACY_STATE;
    }
    const res = await apiCall("billing", "GET");
    if (!res?.success) {
      throw new Error(res?.message || "Failed to load billing");
    }
    const next = {
      ready: true,
      loading: false,
      error: null,
      isLegacy: Boolean(res.data?.isLegacy),
      showPlans: res.data?.showPlans !== false && !res.data?.isLegacy,
      planCode: res.data?.planCode || "free",
      maxEvents: res.data?.maxEvents ?? null,
      allowedTemplateIds: res.data?.allowedTemplateIds || null,
      registrationEnabled: res.data?.registrationEnabled !== false,
      plans: Array.isArray(res.data?.plans) ? res.data.plans : [],
      pendingConfirmationUrl: res.data?.pendingConfirmationUrl || null,
    };
    setState(next);
    return next;
  }, [explicitNew]);

  useEffect(() => {
    if (!installReady) return undefined;
    if (!explicitNew) {
      setState(LEGACY_STATE);
      return undefined;
    }
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true }));
    refresh().catch((err) => {
      if (cancelled) return;
      setState({
        ready: true,
        loading: false,
        isLegacy: false,
        showPlans: true,
        planCode: "free",
        maxEvents: 1,
        allowedTemplateIds: [FREE_TEMPLATE_ID],
        registrationEnabled: false,
        plans: [],
        pendingConfirmationUrl: null,
        error: err?.message || "Failed to load billing",
      });
    });
    return () => {
      cancelled = true;
    };
  }, [installReady, explicitNew, refresh]);

  const selectPlan = useCallback(
    async (planCode) => {
      const host = new URLSearchParams(window.location.search).get("host") || "";
      const res = await apiCall("billing/select", "POST", { planCode, host });
      if (!res?.success) {
        throw new Error(res?.message || "Could not change plan");
      }
      const confirmationUrl = res.data?.confirmationUrl;
      if (confirmationUrl) {
        redirectToShopifyConfirmation(confirmationUrl);
        return res.data;
      }
      await refresh();
      return res.data;
    },
    [refresh],
  );

  const value = useMemo(
    () => ({
      ...state,
      isFreePlan: Boolean(state.ready && !state.isLegacy && state.planCode === "free"),
      refresh,
      selectPlan,
    }),
    [state, refresh, selectPlan],
  );

  return <BillingContext.Provider value={value}>{children}</BillingContext.Provider>;
}

export function useBilling() {
  const ctx = useContext(BillingContext);
  if (!ctx) {
    throw new Error("useBilling must be used within BillingProvider");
  }
  return ctx;
}
